// Generated by Copilot
import { useState } from "react";

import { Callout, Classes } from "@blueprintjs/core";
import { useNavigate } from "react-router";

import { RichContentRenderer } from "@/components/helpers/ui/RichContentRenderer";
import { Box } from "@/components/layout/flexbox";
import { buildComponentMetadata } from "@/components/pages_logged_in/program/components/componentMetadata";
import { ParticipantComponentStatistic } from "@/components/pages_logged_in/program/components/program-component/ParticipantComponentStatistic";
import { SubmitActionItemDialog } from "@/components/pages_logged_in/program/components/SubmitActionItemDialog";
import { ActionItemDisplay } from "@/components/pages_logged_in/program/components/set-components/ActionItemDisplay";
import { canViewComponentStatistic } from "@/components/pages_logged_in/program/components/utils";
import { Button } from "@/components/ui/button";
import { CARD_CLASSES } from "@/css/constants";
import {
  ProgramComponentsQueryDocument,
  ProgramSurveyQueryDocument,
  useSaveProgramSurveyAnswerMutation,
} from "@/graphql";
import { cn } from "@/lib/utils";
import { PanelProps, ParticipantComponent } from "@/types/programs";
import { ensureNonNullArray, formatErrors } from "@/utils/array";

export function ProgramActionItemsPanel({
  component,
  program,
  role = "participant",
  large = false,
  view = "list",
}: PanelProps) {
  const navigate = useNavigate();
  const { participantGuidance, status, survey } =
    component as ParticipantComponent;
  const isProgramLead = role === "lead";
  const canViewStatistic = canViewComponentStatistic(component, isProgramLead);

  const metaData = buildComponentMetadata({ component, isProgramLead });
  const formFields = ensureNonNullArray(survey?.formFields);
  const formFieldAnswers = ensureNonNullArray(survey?.formFieldAnswers);
  const [errors, setErrors] = useState<string[]>([]);

  const [saveProgramSurveyAnswer] = useSaveProgramSurveyAnswerMutation({
    onCompleted: (data) => {
      const ok = data?.saveProgramSurveyAnswer?.ok;
      if (ok) {
        setOpenConfirmDialog(false);
      } else {
        const message = formatErrors(data?.saveProgramSurveyAnswer?.errors);
        setErrors([message || "Something went wrong. Please try again."]);
      }
    },
    onError: (err) => {
      setErrors([err?.message || "Cannot save Program Context"]);
    },
    refetchQueries: [
      {
        query: ProgramSurveyQueryDocument,
        variables: { programSurveyId: component.survey?.id },
      },
      {
        query: ProgramComponentsQueryDocument,
        variables: { programId: program.id },
      },
    ],
    awaitRefetchQueries: true,
  });

  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [actionItemPayload, setActionItemPayload] = useState<{
    fieldId?: string;
    value?: string;
  }>({});

  const isOneQuestionLeft = formFields.length - formFieldAnswers.length === 1;
  const formFieldIds = formFields.map((item) => item.id);
  const answeredFieldIds = formFieldAnswers.map((item) => item.formField?.id);
  const finalFieldId = isOneQuestionLeft
    ? formFieldIds.find((id) => !answeredFieldIds.includes(id))
    : null;

  const _onSubmitAnswerHandler = (fieldId: string, value: string) => {
    if (isOneQuestionLeft) {
      // find form fields don't have in form field answers

      const isClickOnFinalQuestion = finalFieldId === fieldId;
      if (isClickOnFinalQuestion) {
        setOpenConfirmDialog(true);
        setActionItemPayload({ fieldId, value });
        return;
      }
    }

    if (component.survey?.id) {
      saveProgramSurveyAnswer({
        variables: {
          surveyId: component.survey.id,
          answerData: { value, fieldId },
        },
      });
    }
  };

  if (view === "feed") {
    return (
      <div className="xs:p-3.5 xs:rounded-md xs:gap-3 flex flex-col bg-gradient-to-br from-[#F2FAFF] to-[#D8ECF8] md:gap-4 md:rounded-lg md:p-6">
        <div className="text-lg font-semibold text-blue-800">
          You have action items from your recent workshop
        </div>
        <div>
          <Button
            className="xs:px-7 xs:py-2.5 md:px-12 md:py-3"
            onClick={() =>
              navigate(`/programs/${program.id}#component_id_${component.id}`)
            }
            variant="solid"
            intent="primary"
          >
            <span className="xs:text-sm font-semibold md:text-base">
              Go To Checklist
            </span>
          </Button>
        </div>
      </div>
    );
  }

  if (!survey) return null;

  return (
    <Box className="xs:gap-3.5 flex flex-col md:gap-4 empty:hidden">
      <SubmitActionItemDialog
        open={openConfirmDialog}
        onClose={() => {
          setOpenConfirmDialog(false);
          setActionItemPayload({});
        }}
        onConfirm={() => {
          saveProgramSurveyAnswer({
            variables: {
              surveyId: survey.id,
              answerData: actionItemPayload,
            },
          });
        }}
      />

      {canViewStatistic && (
        <ParticipantComponentStatistic
          component={component}
          program={program}
          isProgramLead={isProgramLead}
        />
      )}

      {status !== "Completed" && !canViewStatistic && !!participantGuidance && (
        <div className="flex flex-col gap-2">
          <Box className="text-subtext text-xs font-semibold">
            {metaData.isSurvey ? "GUIDANCE" : ""}
          </Box>
          <Box className={Classes.RUNNING_TEXT}>
            <RichContentRenderer content={participantGuidance} />
          </Box>
        </div>
      )}
      {metaData.visibleActionsItems && metaData.isEligible && (
        <div
          className={cn(
            "xs:p-3.5 xs:gap-5 flex flex-1 flex-col bg-white md:gap-6 md:p-4",
            CARD_CLASSES,
            {
              "max-w-180": !large,
            },
          )}
        >
          {formFields.map((question) => {
            const answer = formFieldAnswers.find(
              (item) => item.formField?.id === question?.id,
            );
            const isComplete = !!answer?.value || !!answer?.userFile;

            return (
              <ActionItemDisplay
                key={question?.id}
                isLead={isProgramLead}
                isComplete={isProgramLead ? false : isComplete}
                answer={answer}
                question={question}
                onSubmitAnswer={(value) => {
                  _onSubmitAnswerHandler(question?.id, value);
                }}
                componentStatus={status}
                isFinalQuestion={finalFieldId === question?.id}
              />
            );
          })}
          <div className="flex flex-col gap-2">
            {errors.map((error, index) => (
              <Callout key={index} intent="danger" icon="warning-sign">
                {error}
              </Callout>
            ))}
          </div>
        </div>
      )}
    </Box>
  );
}
